import {
  Block,
  Animation,
  Callback,
  GameObject,
  Variable,
  Operator,
  Property,
  NumberBlock,
  StringBlock,
  BooleanBlock,
  Time,
  Touch,
  Text,
  Sprite,
  Position,
  Random,
  Scene,
  Direction,
  Axis,
  Key,
  Joystick,
  DPad,
  Sound,
  Ranking,
  Timer,
  Mobile,
  Util,
} from "./index";
import {STATE, GRAMMAR, BLOCK} from "../Type";

export class Action extends Block {
  get type() {
    return "Action";
  }

  get category() {
    return "Action";
  }

  getGrammarFor() {
    return GRAMMAR.VERB;
  }

  getDefaultChildren() {
    switch (this.data) {
      case "set":
      case "say":
      case "playAnimation":
      case "moveTo":
      case "goTo":
      case "setText":
      case "addSize":
      case "setCollision":
      case "changeScene":
      case "sendSignal":
      case "saveScore":
        return [new Block({state: STATE.INSTANCE, parent: this})];
      case "onTouch":
      case "onTouchUp":
      case "onOut":
      case "onFrame":
      case "onShake":
      case "speak":
      case "onClick":
        return [new Callback({state: STATE.INSTANCE, parent: this})];
      case "go":
      case "move":
      case "turn":
      case "rotate":
      case "bind":
        return [new Block({state: STATE.INSTANCE, parent: this}), new Block({state: STATE.INSTANCE, parent: this})];
      case "onSwipe":
      case "wait":
      case "onCollide": // case "onOverlap":
      case "onCollideOnce": // case "onOverlapOnce":
      case "onCollideEnd":
      case "onContact":
      case "onKey":
      case "onKeyUp":
      case "onSignal":
        return [new Block({state: STATE.INSTANCE, parent: this}), new Callback({state: STATE.INSTANCE, parent: this})];
      default:
        return [];
    }
  }

  static getPrototypeBlocks() {
    const prototypeBlocks = [];
    const actionNames = Action.getActionNames();
    for (let i = 0; i < actionNames.length; i++) {
      const actionName = actionNames[i];
      prototypeBlocks.push(new Action({data: actionName, state: STATE.PROTOTYPE}));
    }
    return prototypeBlocks;
  }

  static getActionNames() {
    return [
      "set",
      // "say",
      // "playAnimation",
      // "stopAnimation",
      "move",
      "moveTo",
      "go",
      "goTo",
      "turn",
      // "turnTo",
      "rotate",
      // "addSize",
      // "show",
      // "hide",
      "kill",
      "spawn",
      "revive",
      // "shake",
      // "flash",
      "wait",
      "setText",
      // "setCollision",
      // "sendSignal",
      // "onSignal",
      "onCollide", // "onOverlap",
      "onCollideOnce", // "onOverlapOnce",
      "onCollideEnd",
      "onContact",
      // "onClick",
      // "onKey",
      // "onKeyUp",
      // "onTouch",
      // "onTouchUp",
      // "onOut",
      // "changeScene",
      // "onFrame",
      // "onSwipe",
      // "bind",
      // "play",
      // "restart",
      // "resume",
      // "stop",
      // "pause",
      // "showRanking",
      // "showRankingAscending",
      // "saveScore",
      // "startTimer",
      // "pauseTimer",
      // "resumeTimer",
      // "resetTimer",
      // "onShake",
      // "speak",
      // "vibrate"
    ];
  }

  isAvailableParentFor(block) {
    if (this.data !== "playAnimation") {
      return super.isAvailableParentFor(block);
    }

    const spriteSubject = this.findParent({type: BLOCK.SPRITE});
    if (!spriteSubject) return true;

    const context = this.getParentContext();
    const gameObjectInfo = context.getGameObjectInfoWithName(spriteSubject.data);

    const availableAnimationIds = gameObjectInfo.animationIds || [];
    const animationId = block.data;

    return availableAnimationIds.includes(animationId);
  }

  getAvailableChildTypesAt(index) {
    switch (this.data) {
      case "set":
        if (this.parent instanceof Variable) {
          return [NumberBlock, StringBlock, BooleanBlock, Time, GameObject, Variable, Operator];
        } else if (this.parent instanceof Property) {
          return [...this.parent.getPropertyType(), Variable, Operator];
        } else {
          return [Variable, Operator];
        }
      case "say":
        return [NumberBlock, StringBlock, Time, Variable];
      case "playAnimation":
        return [Animation, Variable];
      case "moveTo":
      case "goTo":
        return [Sprite, Text, Position];
      case "setText":
        return [Variable, StringBlock, NumberBlock];
      case "turnTo":
        return [Sprite, Text, Touch];
      case "wait":
      case "addSize":
        return [NumberBlock, Variable];
      case "setCollision":
        return [Sprite, Screen, Text];
      case "onTouch":
      case "onTouchUp":
      case "onOut":
      case "onFrame":
      case "onShake":
      case "speak":
      case "onClick":
        return [Callback];
      case "changeScene":
        return [Scene];
      case "go":
      case "move":
      case "rotate":
      case "turn":
        return (
          [
            [Direction, Axis],
            [NumberBlock, Variable],
          ][index] || []
        );
      case "onSwipe":
        return [[Direction], [Callback]][index] || [];
      case "onCollide": // case "onOverlap":
      case "onCollideOnce": // case "onOverlapOnce":
      case "onCollideEnd":
      case "onContact":
        return [[Sprite, Text], [Callback]][index] || [];
      case "onKey":
      case "onKeyUp":
        return [[Key], [Callback]][index] || [];
      case "onSignal":
        return [[StringBlock], [Callback]][index] || [];
      case "bind":
        return (
          [
            [Sprite, Text],
            [NumberBlock, Variable],
          ][index] || []
        );
      case "sendSignal":
        return [StringBlock];
      case "saveScore":
        return [NumberBlock, Variable, Time];
      default:
        return [];
    }
  }

  getFinalChildTypesAt(index) {
    switch (this.data) {
      case "set":
        if (this.parent instanceof Property) {
          return [...this.parent.getPropertyType()];
        } else {
          return this.getAvailableChildTypesAt(index);
        }
      case "say":
        return [NumberBlock, StringBlock];
      case "playAnimation":
        return [Animation];
      case "wait":
        return [NumberBlock];
      case "go":
      case "move":
      case "rotate":
      case "turn":
        return [[Direction, Axis], [NumberBlock]][index] || [];
      case "bind":
        return [[Sprite, Text], [NumberBlock]][index] || [];
      default:
        return this.getAvailableChildTypesAt(index);
    }
  }

  getAvailableParentTypes() {
    switch (this.data) {
      case "set":
        return [Variable, Property];
      case "say":
      case "playAnimation":
      case "stopAnimation":
        return [Variable, Sprite];
      case "move":
      case "moveTo":
      case "goTo":
      case "setText":
      case "turn":
      // case "turnTo":
      case "rotate":
      // case "show":
      // case "hide":
      case "kill":
      case "spawn":
      case "revive":
      case "setCollision":
      case "onCollide": // case "onOverlap":
      case "onCollideOnce": // case "onOverlapOnce":
      case "onCollideEnd":
      case "onContact":
      case "onOut":
        return [Variable, Sprite, Text];
      case "shake":
      case "onSwipe":
        return [Variable, Sprite, Screen];
      case "onTouch":
      case "onTouchUp":
      case "onKey":
      case "onKeyUp":
      case "go":
      case "wait":
      case "sendSignal":
      case "onSignal":
      case "addSize":
      case "onClick":
        return [Variable, Sprite, Screen, Text];
      case "flash":
      case "changeScene":
      case "onFrame":
        return [Variable, Screen];
      case "bind":
        return [Variable, Joystick, DPad];
      case "play":
      case "restart":
      case "resume":
      case "stop":
      case "pause":
        return [Variable, Sound];
      case "showRanking":
      case "showRankingAscending":
      case "saveScore":
        return [Ranking];
      case "startTimer":
      case "pauseTimer":
      case "resumeTimer":
      case "resetTimer":
        return [Timer];
      case "onShake":
      case "vibrate":
      case "speak":
        return [Variable, Mobile];
      default:
        return [];
    }
  }

  isDisabledFor(block) {
    if (this.isAnimateAction()) {
      if (block.parent instanceof Sprite) {
        const sprite = block.parent;
        const animationIds = sprite.getAnimationIds();
        return animationIds.length < 1;
      }
    }
    return false;
  }

  isAnimateAction() {
    return this.data === "playAnimation" || this.data === "stopAnimation";
  }

  toJavascript() {
    switch (this.data) {
      case "set":
        return this.toJavascriptForSet();
      case "move":
      case "go":
      case "rotate":
        return this.toJavascriptForMoveGo();
      case "moveTo":
      case "goTo":
        return this.toJavascriptForMoveToGoTo();
      // case "turnTo":
      //   return this.toJavascriptForTurnTo();
      case "say":
        return this.toJavascriptForSay();
      case "playAnimation":
        return this.toJavascriptForPlayAnimation();
      case "setCollision":
        return this.toJavascriptForSetCollision();
      case "bind":
        return this.toJavascriptForBind();
      case "onTouch":
      case "onTouchUp":
        return this.toJavascriptForTouch();
      default:
        if (this.parent instanceof Sound) {
          return this.toJavascriptForSoundParent();
        } else if (this.parent instanceof Util) {
          return this.toJavascriptForUtilParent();
        } else {
          return this.toJavascriptForDefault();
        }
    }
  }

  getArgsToJavascript() {
    let args = "";
    for (let i in this.children) {
      const child = this.children[i];
      if (i !== "0") {
        args += ", ";
      }
      args += child.toJavascript();
    }
    return args;
  }

  toJavascriptForSet() {
    const child = this.children[0];
    const childCode = child.toJavascript();
    if (this.parent instanceof Property) {
      const propertyName = this.parent.data;
      const upperCased = propertyName[0].toUpperCase() + propertyName.slice(1);
      return `.set${upperCased}(${childCode})`;
    } else {
      return ` = ${childCode}`;
    }
  }

  toJavascriptForMoveGo() {
    const distance = this.children[1].toJavascript();
    if (this.children[0] instanceof Direction) {
      const directionBlock = this.children[0];
      switch (directionBlock.data) {
        case "up":
          return `.${this.data}Y(${distance})`;
        case "down":
          return `.${this.data}Y(${distance} * (-1))`;
        case "right":
          return `.${this.data}X(${distance})`;
        case "left":
          return `.${this.data}X(${distance} * (-1))`;
        default:
          return;
      }
    } else if (this.children[0] instanceof Axis) {
      const axisBlock = this.children[0];
      return `.${this.data}${axisBlock.data.toUpperCase()}(${distance})`;
    }
  }

  toJavascriptForMoveToGoTo() {
    if (this.children[0] instanceof GameObject) {
      const gameObject = this.children[0];
      const apiName = `${this.data}Sprite`;
      return `.${apiName}(${gameObject.toJavascript()})`;
    } else if (this.children[0] instanceof Position) {
      const positionBlock = this.children[0];
      const position = positionBlock.data;
      const apiName = `${this.data}`;
      return `.${apiName}(${position.x}, ${position.y}, ${position.z})`;
    } else if (this.children[0] instanceof Random) {
      const apiName = `${this.data}Random`;
      return `.${apiName}()`;
    } else if (this.children[0] instanceof Touch) {
      const apiName = `${this.data}MousePointer`;
      return `.${apiName}()`;
    }
  }

  toJavascriptForTurnTo() {
    if (this.children[0] instanceof GameObject) {
      const gameObject = this.children[0];
      const apiName = `${this.data}Sprite`;
      return `.${apiName}(${gameObject.toJavascript()})`;
    } else if (this.children[0] instanceof Random) {
      const apiName = `${this.data}Random`;
      return `.${apiName}()`;
    } else if (this.children[0] instanceof Touch) {
      const apiName = `${this.data}MousePointer`;
      return `.${apiName}()`;
    }
  }

  toJavascriptForSay() {
    const stringBlock = this.children[0];
    const string = stringBlock.toJavascript();
    const defaultSayTime = 3;
    return `.say(${string}, ${defaultSayTime})`;
  }

  toJavascriptForPlayAnimation() {
    const animationBlock = this.children[0];
    const animationId = animationBlock.toJavascript();
    const defaultRepeat = false;
    return `.playAnimation(${animationId}, ${defaultRepeat})`;
  }

  toJavascriptForSetCollision() {
    if (this.children[0] instanceof Screen) {
      return ".setCollideScene(true)";
    } else if (this.children[0] instanceof GameObject) {
      const gameObject = this.children[0];
      const gameObjectId = gameObject.toJavascript();
      return `.${this.data}(${gameObjectId})`;
    }
  }

  toJavascriptForBind() {
    const gameObjectId = this.children[0].toJavascript();
    const maxSpeed = this.children[1].toJavascript();
    if (this.parent instanceof DPad) {
      return `.onDpad(function(direction){
  var sprite = getSprite(${gameObjectId})
  var speed = ${maxSpeed}
  sprite.setVelocityX(0)
  sprite.setVelocityY(0)
  switch(direction) {
    case "left":
      sprite.setVelocityX(-speed)
      break;
    case "right":
      sprite.setVelocityX(speed)
      break;
    case "up":
      sprite.setVelocityY(-speed)
      break;
    case"down":
      sprite.setVelocityY(speed)
      break;
    default:
      break;
  }
})`;
    } else {
      return `.onJoystick(function(degree, force){
  var maxSpeed = ${maxSpeed}
  var sprite = getSprite(${gameObjectId})
  sprite.setVelocityFromDegree(degree, force, maxSpeed)
})`;
    }
  }

  toJavascriptForTouch() {
    let touchApiName = this.data;
    const args = this.getArgsToJavascript();
    if (this.parent instanceof Screen) {
      touchApiName = {
        onTouch: "onScreenClick",
        onTouchUp: "onScreenClickUp",
      }[touchApiName];
    }
    return `.${touchApiName}(${args})`;
  }

  toJavascriptForSoundParent() {
    const soundApi = `${this.data}Sound`;
    const soundId = this.parent.data;
    return `${soundApi}("${soundId}")`;
  }

  toJavascriptForUtilParent() {
    const apiName = this.data;
    const args = this.getArgsToJavascript();
    return `${apiName}(${args})`;
  }

  toJavascriptForDefault() {
    const apiName = this.data;
    const args = this.getArgsToJavascript();
    return `.${apiName}(${args})`;
  }
}
